/**
 * 商品メイン画像 — ライトボックス（単一画像・左上サムネイル）
 */
if (!customElements.get('yamaga-product-lightbox')) {
  class YamagaProductLightbox extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.root = this.querySelector('.yamaga-product-lightbox');
      this.image = this.querySelector('.yamaga-product-lightbox__image');
      this.caption = this.querySelector('.yamaga-product-lightbox__caption');
      this.content = this.querySelector('.yamaga-product-lightbox__content');
      this.dialog = this.querySelector('.yamaga-product-lightbox__dialog');
      this.backdrop = this.querySelector('.yamaga-product-lightbox__backdrop');
      this.closeButton = this.querySelector('.yamaga-product-lightbox__close');
      this.thumbsNav = this.querySelector('.yamaga-product-lightbox__thumbs');
      this.thumbsList = this.querySelector('.yamaga-product-lightbox__thumbs-list');
      this.opener = null;
      this.closeTimer = null;
      this.galleryItems = [];
      this.activeIndex = 0;

      if (!this.root || !this.image) return;

      if (!this.moved) {
        this.moved = true;
        document.body.appendChild(this);
      }

      this.onKeyUp = this.onKeyUp.bind(this);
      this.close = this.close.bind(this);

      this.backdrop?.addEventListener('click', this.close);
      this.closeButton?.addEventListener('click', this.close);

      this.bindGalleries(document);
      document.addEventListener('yamaga:product-gallery:refresh', this.onGalleryRefresh.bind(this));
      document.addEventListener('shopify:section:load', this.onSectionLoad.bind(this));
    }

    onGalleryRefresh(event) {
      const gallery = event.target?.querySelector?.('.yamaga-product-gallery') || event.target;
      if (gallery?.classList?.contains('yamaga-product-gallery') || gallery?.querySelector?.('.yamaga-product-gallery')) {
        this.bindGalleries(event.target);
      }
    }

    onSectionLoad(event) {
      this.bindGalleries(event.target);
    }

    bindGalleries(scope) {
      (scope || document).querySelectorAll('.yamaga-product-gallery').forEach((gallery) => {
        gallery.querySelectorAll('.product__modal-opener--image .product__media-toggle').forEach((button) => {
          if (button.dataset.yamagaLightboxBound === 'true') return;
          button.dataset.yamagaLightboxBound = 'true';

          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();

              const sourceImg = button.closest('modal-opener')?.querySelector('.media img');
              if (!sourceImg) return;

              this.open(sourceImg, button, gallery);
            },
            true
          );
        });
      });
    }

    getGalleryItems(gallery) {
      if (!gallery) return [];

      const slides = gallery.querySelectorAll(
        '.yamaga-product-gallery__main .swiper-slide[data-media-id], .yamaga-product-gallery--single .product__media-item[data-media-id]'
      );

      return Array.from(slides)
        .map((slide) => {
          const mainImg = slide.querySelector('.product__modal-opener--image .media img');
          if (!mainImg) return null;

          const mediaId = slide.dataset.mediaId;
          const thumbImg =
            gallery.querySelector(`.yamaga-product-gallery__thumb[data-target="${mediaId}"] img`) || mainImg;

          return { mainImg, thumbImg, mediaId };
        })
        .filter(Boolean);
    }

    buildThumbs(items, activeIndex) {
      if (!this.thumbsNav || !this.thumbsList) return;

      this.thumbsList.innerHTML = '';
      this.galleryItems = items;
      this.activeIndex = activeIndex;

      if (items.length <= 1) {
        this.thumbsNav.hidden = true;
        this.thumbsNav.classList.remove('is-visible');
        return;
      }

      items.forEach((item, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'yamaga-product-lightbox__thumb';
        if (index === activeIndex) button.classList.add('is-active');
        button.setAttribute('aria-label', item.mainImg.alt || `Image ${index + 1}`);
        button.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');

        const thumb = document.createElement('img');
        thumb.src = item.thumbImg.currentSrc || item.thumbImg.src;
        thumb.alt = '';
        thumb.width = 120;
        thumb.height = 120;
        thumb.loading = 'eager';
        thumb.decoding = 'async';

        button.appendChild(thumb);
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          this.selectImage(index);
        });

        this.thumbsList.appendChild(button);
      });

      this.thumbsNav.hidden = false;
      this.thumbsNav.classList.add('is-visible');
    }

    updateActiveThumb(index) {
      this.activeIndex = index;
      this.thumbsList?.querySelectorAll('.yamaga-product-lightbox__thumb').forEach((button, i) => {
        const isActive = i === index;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    selectImage(index) {
      const item = this.galleryItems[index];
      if (!item || index === this.activeIndex) return;

      this.updateActiveThumb(index);
      this.content?.classList.add('is-switching');

      window.setTimeout(() => {
        this.copyImage(item.mainImg);
        this.content?.classList.remove('is-switching');
      }, 120);
    }

    open(sourceImg, opener, gallery) {
      const items = this.getGalleryItems(gallery);
      const activeIndex = Math.max(
        0,
        items.findIndex((item) => item.mainImg === sourceImg)
      );

      if (this.root.classList.contains('is-open')) {
        this.selectImage(activeIndex);
        return;
      }

      this.opener = opener;
      this.buildThumbs(items, activeIndex);
      this.copyImage(items[activeIndex]?.mainImg || sourceImg);

      this.root.hidden = false;
      this.root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keyup', this.onKeyUp);

      this.root.classList.remove('is-closing');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.root.classList.add('is-open');
        });
      });

      this.closeButton?.focus({ preventScroll: true });
    }

    close() {
      if (!this.root.classList.contains('is-open')) return;

      this.root.classList.remove('is-open');
      this.root.classList.add('is-closing');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keyup', this.onKeyUp);

      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        this.root.hidden = true;
        this.root.setAttribute('aria-hidden', 'true');
        this.root.classList.remove('is-closing');
        this.image.removeAttribute('src');
        this.image.removeAttribute('srcset');
        this.image.removeAttribute('sizes');
        this.image.alt = '';
        if (this.caption) {
          this.caption.textContent = '';
          this.caption.hidden = true;
        }
        this.thumbsList.innerHTML = '';
        this.thumbsNav.hidden = true;
        this.thumbsNav.classList.remove('is-visible');
        this.galleryItems = [];
        this.activeIndex = 0;

        if (this.opener) {
          this.opener.focus({ preventScroll: true });
          this.opener = null;
        }
      }, 400);
    }

    copyImage(sourceImg) {
      const src = sourceImg.currentSrc || sourceImg.src;
      if (!src) return;

      this.image.src = src;
      this.updateCaption(sourceImg.alt || '');

      if (sourceImg.srcset) {
        this.image.srcset = sourceImg.srcset;
        this.image.sizes = '(min-width: 990px) min(92vw, 1200px), 92vw';
      } else {
        this.image.removeAttribute('srcset');
        this.image.removeAttribute('sizes');
      }

      if (sourceImg.naturalWidth) {
        this.image.width = sourceImg.naturalWidth;
        this.image.height = sourceImg.naturalHeight;
      } else {
        this.image.removeAttribute('width');
        this.image.removeAttribute('height');
      }
    }

    updateCaption(alt) {
      if (!this.caption) return;
      const text = alt.trim();
      this.image.alt = text;
      this.caption.textContent = text;
      this.caption.hidden = !text;
    }

    onKeyUp(event) {
      if (event.code.toUpperCase() === 'ESCAPE') this.close();
    }
  }

  customElements.define('yamaga-product-lightbox', YamagaProductLightbox);
}
